import {HighlightSlide as HighlightSlideType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/highlight.types';
import {
  StoriesSlide,
  desktopAndUp,
  tabletAndUp,
  useInView,
} from '@aboutyou/ui-components';
import React, {useDeferredValue, useEffect, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {transformVideo} from '../../../../features/ContentBlocks/transforms';
import {BrandHighlightCtaTracked} from '../../BrandHubTracking/BrandHighlightCtaTracked';
import {useHighlightZoomSliderTracking} from '../../BrandHubTracking/hooks/useHighlightZoomSliderTracking';

export const HighlightSlide: React.FC<{
  slides: Array<HighlightSlideType>;
  highlightId: number;
  isHidden?: boolean;
  isActive?: boolean;
  handleSlideInViewChange: (isInView: boolean) => void;
  onStoryEnd?: () => void;
}> = ({
  slides,
  highlightId,
  isHidden,
  isActive,
  handleSlideInViewChange,
  onStoryEnd,
}) => {
  const {inView, ref} = useInView<HTMLDivElement>({
    threshold: 0.5,
    initialInView: true,
  });
  const deferredInView = useDeferredValue(inView);

  // eslint-disable-next-line local-rules/prefer-use-inview-effect -- disabled because inView is also used in the JSX
  useEffect(() => {
    handleSlideInViewChange(inView);
  }, [inView]);

  const normalizedSlides = useMemo(
    () =>
      normalizeSlides({
        slides,
        highlightId,
        isHidden,
      }),
    [highlightId, slides, isHidden],
  );

  const track = useHighlightZoomSliderTracking();

  const onSlideStart = (slideIndex: number) => {
    const slideToTrack = slides[slideIndex];
    if (!slideToTrack) return;

    track({
      streamPosition: slideIndex,
      slide: slideToTrack,
      highlightId,
    })('impression');
  };

  return (
    <OuterWrapper data-hidden={isHidden}>
      <StyledStoriesSlide
        ref={ref}
        $inView={deferredInView}
        isActive={isActive}
        slides={normalizedSlides}
        onStoryEnd={onStoryEnd}
        onSlideStart={isHidden ? undefined : onSlideStart}
      />
    </OuterWrapper>
  );
};

const OuterWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 0;

  &[data-hidden='true'] {
    opacity: 0;
    pointer-events: none;
    flex-basis: 30%;
  }
`;

const StyledStoriesSlide = styled(StoriesSlide)<{
  $inView: boolean;
}>`
  display: flex;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;

  transform-origin: 0% 0%;
  transition: transform 0.3s ease-in-out, filter 0.3s ease-in-out;
  transform-origin: 50% 50%;

  ${tabletAndUp(css`
    filter: brightness(0.3);
    transform: scale(0.4);
  `)}

  ${({$inView}) =>
    $inView &&
    css`
      ${tabletAndUp(css`
        transform: scale(0.6);
      `)}
    `}

  ${({isActive}) =>
    isActive &&
    css`
      ${tabletAndUp(css`
        filter: brightness(1);
        transform: scale(1);
      `)}
      ${desktopAndUp(css`
        filter: brightness(1);
        transform: scale(0.8);
      `)}
    `}
`;

function normalizeSlides({
  slides,
  highlightId,
  isHidden,
}: {
  slides: HighlightSlideType[];
  highlightId: number;
  isHidden?: boolean;
}) {
  return slides.map((slide, index) => {
    return {
      ...(slide.content.imageOrVideo?.$case === 'image'
        ? {image: slide.content.imageOrVideo.image}
        : {video: transformVideo(slide.content.imageOrVideo!.video)}),
      durationInMilliseconds: slide.duration.seconds * 1000,
      ...(slide.cta
        ? {
            cta: {
              link: slide.cta.link,
              text: slide.cta.text.text,
              color: slide.cta.text.color,
              CtaTrackedComponent: React.memo(function CtaTrackedComponent({
                children,
              }: React.PropsWithChildren) {
                if (isHidden) {
                  return children;
                }
                return (
                  <BrandHighlightCtaTracked
                    position={index}
                    highlightId={highlightId}
                    highlightSlide={slide}
                  >
                    {children}
                  </BrandHighlightCtaTracked>
                );
              }),
            } as const,
          }
        : {}),
    };
  });
}
